import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  Button
} from '@mui/material';
import { Delete as DeleteIcon, Check as CheckIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useGetOnBehalfUsers, useDeleteOnBehalfUser, OnBehalfUser } from '../../../hooks/api/onBehalfUser';
import DashboardLayout from '../../../components/layout/layout';
import moment from 'moment';
import { styled } from "@mui/material/styles";
import useRequest from '../../../hooks/useRequest';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

type SortField = 'userId' | 'firstName' | 'lastName' | 'userEmail' | 'accessLevel' | 'addedBy' | 'addedAt';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

const OnBehalfUsersManagement: React.FC = () => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<OnBehalfUser | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Add user state
  const [userEmailInput, setUserEmailInput] = useState<string>('');
  const [isAddingUser, setIsAddingUser] = useState<boolean>(false);
  
  // Company restrictions state - ALWAYS required (now accepts customer numbers)
  const [customerNumbersInput, setCustomerNumbersInput] = useState<string>('');
  
  // Edit user state
  const [editingUser, setEditingUser] = useState<OnBehalfUser | null>(null);
  const [editCustomerNumbers, setEditCustomerNumbers] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editError, setEditError] = useState<string>('');

  // Sorting state (removed pagination)
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  // API hooks
  const { data: users, isLoading, error: fetchError } = useGetOnBehalfUsers();
  const deleteUser = useDeleteOnBehalfUser();
  const request = useRequest();

  // Sorting logic (removed pagination)
  const sortedUsers = useMemo(() => {
    const list = users || [];
    if (!sortState.field || !sortState.direction) return list;
    return [...list].sort((a, b) => {
      let aValue: any = a[sortState.field!];
      let bValue: any = b[sortState.field!];
      
      // Handle special cases
      if (sortState.field === 'firstName' || sortState.field === 'lastName') {
        // For name sorting, use the actual field
        aValue = a[sortState.field];
        bValue = b[sortState.field];
      } else if (sortState.field === 'addedAt') {
        // Handle date sorting
        if (!aValue) aValue = '1900-01-01';
        if (!bValue) bValue = '1900-01-01';
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortState.field === 'userId') {
        // Handle numeric sorting
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else {
        // Handle string sorting
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (sortState.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [users, sortState]);

  // Only show error if it's a real error, not an empty array
  const shouldShowError = fetchError && !Array.isArray(users);

  const handleDelete = async (user: OnBehalfUser) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteUser.mutateAsync(userToDelete.id);
      setSuccess(`User ${userToDelete.firstName} ${userToDelete.lastName} removed from on-behalf list successfully!`);
      setOpenDeleteDialog(false);
      setUserToDelete(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to remove user from list');
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  // Add user handler - REMOVED: Simple add without restrictions is no longer allowed
  // All users must have company restrictions

  // Add user with company restrictions handler
  const handleAddUserWithRestrictions = async () => {
    if (!userEmailInput.trim()) {
      setError('Please enter a User Email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmailInput.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (!customerNumbersInput.trim()) {
      setError('Customer numbers are required for all on-behalf users');
      return;
    }

    // Parse customer numbers (comma or space separated) - keep as strings
    const customerNumbers = customerNumbersInput
      .split(/[,\s]+/)
      .map(num => num.trim())
      .filter(num => num.length > 0);

    if (customerNumbers.length === 0) {
      setError('Please enter valid Customer Numbers');
      return;
    }

    // Check for duplicate customer numbers
    const uniqueCustomerNumbers = Array.from(new Set(customerNumbers));
    if (uniqueCustomerNumbers.length !== customerNumbers.length) {
      const duplicates = customerNumbers.filter((num, index) => customerNumbers.indexOf(num) !== index);
      setError(`Duplicate customer numbers found: ${Array.from(new Set(duplicates)).join(', ')}`);
      return;
    }

    setIsAddingUser(true);
    setError('');
    setSuccess('');

    try {
      const response = await request({
        method: 'post',
        url: `/api/v1/admin/on-behalf-users/add-user-with-restrictions`,
        payload: {
          userEmail: userEmailInput.trim(),
          customerNumbers: customerNumbers
        },
        config: {},
        completeResponse: false,
      });

      setSuccess(`User ${response.firstName} ${response.lastName} (Email: ${response.userEmail}) added successfully with company accounts!`);
      setUserEmailInput('');
      setCustomerNumbersInput('');
      
      // Refresh the users list
      window.location.reload();
    } catch (err: any) {
      console.error('Error adding user with restrictions:', err);
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error || 'Failed to add user. Please try again.';
      setError(errorMessage);
    } finally {
      setIsAddingUser(false);
    }
  };

  // Edit user handler
  const handleEditUser = (user: OnBehalfUser) => {
    setEditingUser(user);
    // Extract customer numbers from allowedCompanies (which has custNum)
    const customerNumbers = user.allowedCompanies && user.allowedCompanies.length > 0
      ? user.allowedCompanies.map((c: { custNum?: string }) => c.custNum || '').filter((c: string) => c).join(', ')
      : '';
    setEditCustomerNumbers(customerNumbers);
    setIsEditing(true);
    setEditError('');
    setError('');
    setSuccess('');
  };

  // Cancel edit handler
  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditCustomerNumbers('');
    setIsEditing(false);
    setEditError('');
    setError('');
    setSuccess('');
  };

  // Update user company restrictions handler
  const handleUpdateUserRestrictions = async () => {
    if (!editingUser) return;

    if (!editCustomerNumbers.trim()) {
      setEditError('Customer numbers are required for all on-behalf users');
      return;
    }

    // Parse customer numbers (comma or space separated) - keep as strings
    const customerNumbers = editCustomerNumbers
      .split(/[,\s]+/)
      .map(num => num.trim())
      .filter(num => num.length > 0);

    if (customerNumbers.length === 0) {
      setEditError('Please enter valid Customer Numbers');
      return;
    }

    // Check for duplicate customer numbers
    const uniqueCustomerNumbers = Array.from(new Set(customerNumbers));
    if (uniqueCustomerNumbers.length !== customerNumbers.length) {
      const duplicates = customerNumbers.filter((num, index) => customerNumbers.indexOf(num) !== index);
      setEditError(`Duplicate customer numbers found: ${Array.from(new Set(duplicates)).join(', ')}`);
      return;
    }

    setIsAddingUser(true);
    setEditError('');
    setSuccess('');

    try {
      const response = await request({
        method: 'put',
        url: `/api/v1/admin/on-behalf-users/${editingUser.id}/company-restrictions`,
        payload: {
          customerNumbers: customerNumbers,
          restrictionType: 'RESTRICTED'
        },
        config: {},
        completeResponse: false,
      });

      setSuccess(`Company accounts updated successfully for ${editingUser.firstName} ${editingUser.lastName}!`);
      handleCancelEdit();
      
      // Refresh the users list
      window.location.reload();
    } catch (err: any) {
      console.error('Error updating user restrictions:', err);
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error || 'Failed to update user restrictions. Please try again.';
      setEditError(errorMessage);
    } finally {
      setIsAddingUser(false);
    }
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    setSortState(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortState.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortState.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  if (isLoading) {
    return (
      <DashboardLayout 
        title="On-Behalf User Management" 
        initialBreadcrumbs="Admin"
        levelBreadcrumbs="On-Behalf User Management"
        breadcrumbs="On-Behalf User Management"
        activeMenu="admin"
        containerTitle="On-Behalf User Management"
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Loading...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="On-Behalf Order Entry Management" 
      initialBreadcrumbs="Admin" 
      levelBreadcrumbs="On-Behalf Order Entry"
      breadcrumbs="On-Behalf Order Entry Management"
      activeMenu="admin"
      containerTitle="On-Behalf Order Entry Users"
    >
      {/* Error Alert (only if there is a real error) */}
      {shouldShowError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load on-behalf users: {fetchError.message}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" color="#c3183c" gutterBottom fontWeight="bold">
            On-Behalf Order Entry Users
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Users with on-behalf order entry access
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px', fontStyle: 'italic' }}>
            All on-behalf users must have company accounts (Channel Partner or Customer). Enter Customer Numbers separated by commas or spaces (e.g., 32969, 12266, 32964)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            label="User Email"
            value={userEmailInput}
            onChange={(e) => setUserEmailInput(e.target.value)}
            placeholder="Enter User Email"
            type="email"
            size="small"
            sx={{ width: 250 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddUserWithRestrictions();
              }
            }}
          />
          
          <TextField
            label="Customer Numbers *"
            value={customerNumbersInput}
            onChange={(e) => setCustomerNumbersInput(e.target.value)}
            placeholder="Enter Customer Numbers (comma or space separated)"
            size="small"
            sx={{ width: 300 }}
            required
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddUserWithRestrictions();
              }
            }}
          />
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUserWithRestrictions}
            disabled={isAddingUser || !userEmailInput.trim() || !customerNumbersInput.trim()}
            sx={{ 
              backgroundColor: '#c3183c',
              '&:hover': {
                backgroundColor: '#a01530'
              },
              px: 2,
              py: 1
            }}
          >
            {isAddingUser ? 'Adding...' : 'Add User'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => {
              setUserEmailInput('');
              setCustomerNumbersInput('');
              setError('');
              setSuccess('');
            }}
            sx={{ 
              borderColor: '#c3183c',
              color: '#c3183c',
              '&:hover': {
                borderColor: '#a01530',
                backgroundColor: 'rgba(195, 24, 60, 0.04)'
              },
              px: 2,
              py: 1
            }}
          >
            Clear Fields
          </Button>
        </Box>
      </Box>



      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCol><strong>Actions</strong></TableCol>
              <TableCol 
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                onClick={() => handleSort('userId')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong>User ID</strong>
                  <Tooltip title="Click to sort">{getSortIcon('userId')}</Tooltip>
                </Box>
              </TableCol>
              <TableCol 
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                onClick={() => handleSort('firstName')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong>Name</strong>
                  <Tooltip title="Click to sort">{getSortIcon('firstName')}</Tooltip>
                </Box>
              </TableCol>
              <TableCol 
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                onClick={() => handleSort('userEmail')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong>Email</strong>
                  <Tooltip title="Click to sort">{getSortIcon('userEmail')}</Tooltip>
                </Box>
              </TableCol>
              <TableCol 
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                onClick={() => handleSort('accessLevel')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong>Access Level</strong>
                  <Tooltip title="Click to sort">{getSortIcon('accessLevel')}</Tooltip>
                </Box>
              </TableCol>
              <TableCol><strong>Admin</strong></TableCol>
              <TableCol><strong>Customer Numbers</strong></TableCol>
              <TableCol 
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                onClick={() => handleSort('addedBy')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong>Added By</strong>
                  <Tooltip title="Click to sort">{getSortIcon('addedBy')}</Tooltip>
                </Box>
              </TableCol>
              <TableCol 
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                onClick={() => handleSort('addedAt')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong>Added Date</strong>
                  <Tooltip title="Click to sort">{getSortIcon('addedAt')}</Tooltip>
                </Box>
              </TableCol>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.length === 0 ? (
              <TableRow>
                <TableCol colSpan={9} align="center">
                  <Box py={3}>
                    <Typography color="textSecondary" gutterBottom>
                      No users have been added to the on-behalf order entry list yet.
                    </Typography>
                  </Box>
                </TableCol>
              </TableRow>
            ) : (
              sortedUsers.map((user) => (
                <TableRow 
                  key={user.id} 
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    backgroundColor: editingUser?.id === user.id ? 'rgba(25, 118, 210, 0.04)' : 'inherit'
                  }}
                >
                  <TableCol>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                      <Tooltip title={`Edit company accounts for ${user.firstName} ${user.lastName}`}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditUser(user)}
                          color="primary"
                          disabled={isEditing && editingUser?.id !== user.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Remove ${user.firstName} ${user.lastName} from on-behalf list`}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(user)}
                          color="error"
                          disabled={isEditing}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol>{user.userId}</TableCol>
                  <TableCol>{`${user.firstName} ${user.lastName}`}</TableCol>
                  <TableCol>{user.userEmail}</TableCol>
                  <TableCol>{user.accessLevel}</TableCol>
                  <TableCol>
                    <Box display="flex" justifyContent="center" alignItems="center">
                      {user.admin ? (
                        <CheckIcon 
                          color="success" 
                          fontSize="small"
                          sx={{ 
                            color: '#c3183c',
                            fontSize: '20px',
                            '&:hover': {
                              transform: 'scale(1.1)'
                            },
                            transition: 'transform 0.2s ease-in-out'
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCol>
                  <TableCol>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '12px',
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        lineHeight: 1.4
                      }}
                    >
                      {user.allowedCompanies && user.allowedCompanies.length > 0
                        ? user.allowedCompanies.map((c: { custNum?: string }) => c.custNum || '').filter((c: string) => c).join(', ')
                        : '-'
                      }
                    </Typography>
                  </TableCol>
                  <TableCol>{user.addedBy}</TableCol>
                  <TableCol>{moment(user.addedAt).format('MM/DD/YYYY HH:mm')}</TableCol>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Back Button (removed pagination) */}
      <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
        <Button 
          variant="contained" 
          sx={{ 
            backgroundColor: '#c3183c', 
            padding: '6px 25px !important',
            '&:hover': {
              backgroundColor: '#a01530'
            }
          }} 
          onClick={() => window.history.back()}
        >
          BACK
        </Button>
      </Box>

      {/* Confirmation Dialog for Deletion */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
            minWidth: '400px',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        <DialogContent sx={{ pt: 4, pb: 3, px: 4, textAlign: 'center' }}>
          <Typography id="delete-dialog-description" variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              sx={{
                backgroundColor: '#c3183c',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#a01530'
                },
                px: 4,
                py: 1,
                minWidth: '80px'
              }}
            >
              YES
            </Button>
            <Button
              onClick={handleCancelDelete}
              variant="contained"
              sx={{
                backgroundColor: '#c3183c',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#a01530'
                },
                px: 4,
                py: 1,
                minWidth: '80px'
              }}
            >
              NO
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit User Company Restrictions Dialog */}
      <Dialog
        open={isEditing}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ color: '#c3183c', fontWeight: 'bold' }}>
            Edit Company Accounts
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {editingUser && `${editingUser.firstName} ${editingUser.lastName} (ID: ${editingUser.userId})`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Update the companies this user can place orders for:
            </Typography>
            {editingUser?.allowedCompanies && editingUser.allowedCompanies.length > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                Current customer numbers: {editingUser.allowedCompanies.map((c: { custNum?: string }) => c.custNum || '').filter((c: string) => c).join(', ')}
              </Typography>
            )}
            <TextField
              label="Customer Numbers *"
              value={editCustomerNumbers}
              onChange={(e) => setEditCustomerNumbers(e.target.value)}
              placeholder="Enter Customer Numbers (comma or space separated)"
              fullWidth
              required
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateUserRestrictions();
                }
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Enter Customer Numbers separated by commas or spaces (e.g., 32969, 12266, 32964)
            </Typography>
            
            {/* Edit Error Display */}
            {editError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {editError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCancelEdit}
            variant="outlined"
            sx={{ borderColor: '#c3183c', color: '#c3183c' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateUserRestrictions}
            variant="contained"
            disabled={isAddingUser || !editCustomerNumbers.trim()}
            sx={{
              backgroundColor: '#c3183c',
              '&:hover': { backgroundColor: '#a01530' }
            }}
          >
            {isAddingUser ? 'Updating...' : 'Update Accounts'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default OnBehalfUsersManagement; 